import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ProfileForm from "@/components/portal/ProfileForm";
import EventForm from "@/components/portal/EventForm";
import ReferralForm from "@/components/portal/ReferralForm";
import ReferralList from "@/components/portal/ReferralList";
import MyReferralStats from "@/components/portal/MyReferralStats";
import MyEventsList from "@/components/portal/MyEventsList";
import PostManager from "@/components/portal/PostManager";
import ShareApplyLink from "@/components/portal/ShareApplyLink";
import MyCreatedEvents from "@/components/portal/MyCreatedEvents";
import RenewalTab from "@/components/portal/RenewalTab";
import { useMyRoles } from "@/hooks/useMyRoles";
import { useRoleCheck } from "@/hooks/useRoleCheck";
import { Shield, Send, Calendar, BarChart3, FileText, Settings, PlusCircle, User, CreditCard } from "lucide-react";


const Portal = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [referralRefreshKey, setReferralRefreshKey] = useState(0);
  const [statsKey, setStatsKey] = useState(0);
  const [activeTab, setActiveTab] = useState("referrals");
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAnyAdminRole } = useMyRoles();
  const { hasRole: canManageBlog } = useRoleCheck("blog_admin");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadMemberId = async (userId: string) => {
      try {
        const { data: mid } = await supabase.rpc("get_member_id_for_user", { _user_id: userId });
        if (!cancelled) setMemberId(mid ?? null);
      } catch {
        /* non-critical */
      }
    };

    const applySession = (session: Awaited<ReturnType<typeof supabase.auth.getSession>>["data"]["session"]) => {
      if (cancelled) return;

      if (!session) {
        setLoading(false);
        navigate("/auth");
        return;
      }

      setUser(session.user);
      setLoading(false);
      loadMemberId(session.user.id);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Defer to avoid Supabase auth deadlock (no awaited calls inside callback)
      setTimeout(() => applySession(session), 0);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    }).catch(() => {
      if (!cancelled) {
        setLoading(false);
        navigate("/auth");
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("Payment successful — your membership is up to date.");
      setActiveTab("renewal");
      searchParams.delete("checkout");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    } else if (checkout === "cancelled") {
      toast.error("Payment was cancelled. You can try again from the Renewal tab.");
      setActiveTab("renewal");
      searchParams.delete("checkout");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  const totalCols = 7 + (canManageBlog ? 1 : 0);
  const lgGridColsClass: Record<number, string> = {
    7: "lg:grid-cols-7",
    8: "lg:grid-cols-8",
  };

  const tabItems = [
    { value: "referrals", label: "Referrals", Icon: Send },
    { value: "stats", label: "My Stats", Icon: BarChart3 },
    { value: "my-events", label: "My Events", Icon: Calendar },
    { value: "events", label: "Create Event", Icon: PlusCircle },
    { value: "my-created-events", label: "Manage Events", Icon: Settings },
    ...(canManageBlog ? [{ value: "stories", label: "Stories", Icon: FileText }] : []),
    { value: "profile", label: "My Profile", Icon: User },
    { value: "renewal", label: "Renewal", Icon: CreditCard },
  ];

  const handleTabChange = (v: string) => {
    setActiveTab(v);
    if (v === "stats") setStatsKey((k) => k + 1);
  };


  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 pt-20 md:pt-36 lg:pt-20 pb-20">
        <div className="max-w-4xl mx-auto">
          {/* Header: stacked on mobile, inline on desktop */}
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="text-center md:text-left">
              <h1 className="text-3xl md:text-4xl font-bold">Member Portal</h1>
              <p className="text-muted-foreground mt-1 md:mt-2 text-sm md:text-base break-words">
                Welcome back, {user?.email}
              </p>
            </div>
            <div className="flex gap-2 justify-center md:justify-end">
              {hasAnyAdminRole && (
                <Button variant="outline" size="sm" className="md:h-10 md:px-4" onClick={() => navigate("/admin")}>
                  <Shield className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              )}
              <Button variant="outline" size="sm" className="md:h-10 md:px-4" onClick={handleLogout}>
                Sign Out
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            {/* Mobile/tablet: dropdown selector. Desktop: tab row. */}
            <div className="lg:hidden">
              <Select value={activeTab} onValueChange={handleTabChange}>
                <SelectTrigger className="w-full h-12 text-base" aria-label="Select section">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tabItems.map(({ value, label, Icon }) => (
                    <SelectItem key={value} value={value}>
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0" />
                        {label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="hidden lg:block">
              <TabsList className={`grid h-auto w-full gap-2 px-1 py-1 ${lgGridColsClass[totalCols]}`}>
                {tabItems.map(({ value, label, Icon }) => (
                  <TabsTrigger key={value} value={value} className="flex items-center gap-1.5 whitespace-nowrap px-3 py-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value="referrals" className="mt-6 space-y-6">
              <ReferralForm userId={user?.id} onReferralCreated={() => setReferralRefreshKey((k) => k + 1)} />
              <ReferralList userId={user?.id} refreshKey={referralRefreshKey} />
              <ShareApplyLink />
            </TabsContent>
            <TabsContent value="stats" className="mt-6">
              <MyReferralStats key={statsKey} userId={user?.id} />
            </TabsContent>
            <TabsContent value="my-events" className="mt-6">
              <MyEventsList />
            </TabsContent>
            <TabsContent value="events" className="mt-6">
              <EventForm userId={user?.id} />
            </TabsContent>
            <TabsContent value="my-created-events" className="mt-6">
              <MyCreatedEvents userId={user?.id} />
            </TabsContent>
            {canManageBlog && (
              <TabsContent value="stories" className="mt-6">
                <PostManager authorMemberId={memberId} />
              </TabsContent>
            )}
            <TabsContent value="profile" className="mt-6">
              <ProfileForm userId={user?.id} />
            </TabsContent>
            <TabsContent value="renewal" className="mt-6">
              <RenewalTab userId={user?.id} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Portal;
